import { DocumentService } from './documentService'
import { defaultDocumentDisplay, documentDisplayPayload } from '@/helpers/documentDisplay'

const PATH = '/v1/int/document-settings/current'

/**
 * Service for reading and writing the tenant's document custom field registry, through the document
 * settings REST API. Unlike RefundService or ReturnSettingsService, it does not own an axios client:
 * it reuses the one DocumentService builds, so the bearer auth and the token refresh interceptor are
 * shared with the document calls rather than duplicated.
 * The api reads and accepts `custom_fields` for an octipas-admin only, and refuses to lose a key it
 * already stores, which is why the screen deactivates a field instead of deleting it.
 */
export class DocumentSettingsService {
  /**
   * Reuse the authenticated document API transport.
   * @param {Object} auth Authentication context used by DocumentService.
   */
  constructor(auth) {
    // Reuse the document API's authenticated Axios transport and token refresh.
    this.http = new DocumentService(auth).documentAPI
  }

  /**
   * Read the whole tenant configuration: both tabs of the settings screen live in one document, so
   * one call serves them. Defaults are used only when the configuration does not exist yet; an
   * undeployed route or a refused access must not look like a tenant without settings.
   * @returns {Promise<Object>} Display settings, available fields, and the registry when permitted.
   */
  async getSettings() {
    try {
      const { data } = await this.http.get(PATH)
      return this.readDisplay(data)
    } catch (error) {
      if (error.response?.status === 404 && error.response?.data?.message === 'document_settings_not_found') {
        return defaultDocumentDisplay()
      }
      throw error
    }
  }

  /**
   * Write the sections the editor actually changed. The api reads only the keys the body carries, so
   * an omitted section is left as it stands — and a section sent along is validated against the other
   * one in the same operation, which is how a key can be activated and selected as a column at once.
   * @param {Object} body Sections to write: `menu_roles` and `list`, `custom_fields`, or both.
   * @returns {Promise<Object>} Validated configuration returned by the API.
   */
  async saveSettings(body) {
    const { data } = await this.http.put(PATH, body)
    return this.readDisplay(data)
  }

  /**
   * Validate the display response structure before exposing it to the editor.
   * @param {Object} data API response body.
   * @returns {Object} Display settings with the tenant display dictionary.
   * @throws {Error} When required configuration collections are missing.
   */
  readDisplay(data) {
    if (
      !Array.isArray(data?.menu_roles) ||
      !Array.isArray(data?.list?.columns) ||
      !Array.isArray(data?.list?.default_filters?.document_types) ||
      !Array.isArray(data?.list?.default_filters?.entity_types) ||
      !Array.isArray(data?.available_fields)
    ) {
      throw new Error('Invalid document display settings response')
    }
    // custom_fields is left untouched and unvalidated: the api omits it for anyone but an
    // octipas-admin, and that absence is the signal the registry tab reads to stay hidden.
    return {
      ...documentDisplayPayload(data),
      available_fields: data.available_fields,
      custom_fields: data.custom_fields,
    }
  }
}
